/* AiSetupFlow — per-account guided AI configuration. Route /account/:accountId/ai-setup.
   purpose → (talkDepth | monet) → aggression → folders → telegram → modelInfo → ready.

   Mapping (resolved in design): talkDepth → ai_base_regime; aggression → ai_hot_transition_mode
   (balanced=moderate); PPV → talk_sext_and_sales_ladder; tips-in-sexting →
   talk_sext_and_hot_tip. Both sexting money-makers share the single regime field, so they are
   mutually exclusive — precedence ppv > tipsext. normal tips → normal_tip_policies.is_active;
   VIP → vip_offer_policies.is_active + page_url; folders → lists_to_ignore; telegram →
   telegram_chat_id (saved before the test send).

   Paid sexting is NOT offered here: it was decommissioned fleet-wide by ADR 0012. Because the
   gate arms on the EXISTENCE of an active paid_sexting_policies row (brain ADR 0007), any
   surface that writes is_active=true silently re-arms it on the account — which is exactly
   what this flow did for 10 accounts between 2026-09-13 and 09-21. Do not re-add the option
   without reversing that ADR. */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAccounts } from '../../context/AccountsContext';
import { OnboardingStage } from './OnboardingStage';
import { Panel, Head, Title, Btn, Option, Icon, C, Pose } from './onboardingKit';
import { Spinner, BusyLabel } from '../Spinner';

type Step = 'purpose' | 'talkDepth' | 'monet' | 'aggression' | 'folders' | 'telegram' | 'modelInfo' | 'ready';
type Purpose = 'support' | 'monetise' | 'unsure';
type TalkDepth = 'only_talk' | 'talk_and_tease' | 'talk_and_sext';
type Aggression = 'shy' | 'balanced' | 'horny';
type Monet = 'ppv' | 'tipsext' | 'tipchat' | 'vip';

const POSE_FOR: Record<Step, Pose> = {
  purpose: 'point', talkDepth: 'explain', monet: 'explain', aggression: 'think',
  folders: 'explain', telegram: 'point', modelInfo: 'explain', ready: 'okay',
};
const HOT_MODE: Record<Aggression, string> = { shy: 'shy', balanced: 'moderate', horny: 'horny' };

export function AiSetupFlow() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  // Leaving the flow: bring the shell's Accounts list up to date with what changed here.
  const { refresh: refreshAccounts } = useAccounts();
  useEffect(() => () => { void refreshAccounts(); }, [refreshAccounts]);
  const { accountId = '' } = useParams();

  const [step, setStep] = useState<Step>('purpose');
  const [hist, setHist] = useState<Step[]>([]);
  const [animKey, setAnimKey] = useState(0);

  const [purpose, setPurpose] = useState<Purpose | null>(null);
  const [talkDepth, setTalkDepth] = useState<TalkDepth | null>(null);
  const [monet, setMonet] = useState<Monet[]>([]);
  const [aggression, setAggression] = useState<Aggression>('balanced');
  const [aggressionTouched, setAggressionTouched] = useState(false);
  const [folders, setFolders] = useState<string[]>(['Notifications']);
  const [folderOpts, setFolderOpts] = useState<string[]>([]);
  const [folderOpen, setFolderOpen] = useState(false);
  const [vipUrl, setVipUrl] = useState('');
  const [tgId, setTgId] = useState('');
  const [tgStatus, setTgStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [tgError, setTgError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);

  // Hydrate from the account: current ignore-list, telegram id, and the folder options.
  useEffect(() => {
    if (!accountId) return;
    let active = true;
    (async () => {
      try {
        const acc = await api.getAccount(accountId);
        if (!active) return;
        if (acc.lists_to_ignore?.length) setFolders(acc.lists_to_ignore);
        if (acc.telegram_chat_id) setTgId(acc.telegram_chat_id);
      } catch { /* ignore */ }
      // Pull both real platform vaults and lists live now. A manually-provisioned
      // account may not have gone through the connection webhook, and Fansly's
      // folders otherwise remain absent from of_vaults until this explicit sync.
      // Falls back to whatever's already stored, then to a static set.
      setFoldersLoading(true);
      try {
        const [lists] = await Promise.all([
          api.syncAccountLists(accountId),
          api.syncAccountVaults(accountId).catch(() => null),
        ]);
        if (active && lists.length) setFolderOpts(lists.map((l) => l.of_list_name).filter(Boolean));
      } catch {
        try {
          const stored = await api.getAccountOfLists(accountId);
          if (active && stored.length) setFolderOpts(stored.map((l) => l.of_list_name).filter(Boolean));
        } catch { /* fresh account / no lists — static fallback is used */ }
      } finally {
        if (active) setFoldersLoading(false);
      }
    })();
    return () => { active = false; };
  }, [accountId]);

  const go = (s: Step) => { setHist((h) => [...h, step]); setStep(s); setAnimKey((k) => k + 1); };
  const back = () => setHist((h) => {
    const prev = h[h.length - 1];
    if (!prev) return h;
    setStep(prev); setAnimKey((k) => k + 1);
    return h.slice(0, -1);
  });
  const toDashboard = () => navigate('/dashboard', { replace: true });

  // Progress sequence (drives dots), excluding the terminal "ready".
  const seq: Step[] = (() => {
    const s: Step[] = ['purpose'];
    if (purpose === 'support') { s.push('talkDepth'); if (talkDepth && talkDepth !== 'only_talk') s.push('aggression'); }
    else if (purpose === 'monetise') { s.push('monet', 'aggression'); }
    else if (purpose === 'unsure') { s.push('aggression'); }
    s.push('folders', 'telegram', 'modelInfo');
    return s;
  })();
  const dotN = seq.length;
  const dotIndex = Math.max(0, seq.indexOf(step));
  const stepLabel = t('common.step', { i: dotIndex + 1, n: dotN });

  const toggleMonet = (m: Monet) => setMonet((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);
  const toggleFolder = (f: string) => setFolders((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);
  const pickAggression = (a: Aggression) => { setAggression(a); setAggressionTouched(true); };

  const sendTelegramTest = async () => {
    setTgStatus('sending'); setTgError(null);
    try {
      await api.updateAccount(accountId, { telegram_chat_id: tgId.trim() });
      await api.testTelegramNotification(accountId);
      setTgStatus('ok');
    } catch (err) {
      setTgStatus('error');
      setTgError(err instanceof Error ? err.message : t('aiSetup.telegram.error'));
    }
  };

  // Compute the regime + hot-transition writes from the user's answers.
  const applySettings = async () => {
    setSaving(true);
    try {
      // Stamp completion so the "Finish setup" entry on the account page hides.
      const payload: Record<string, unknown> = { lists_to_ignore: folders, ai_setup_completed_at: new Date().toISOString() };
      let regime: string | null = null;
      if (purpose === 'support' && talkDepth) regime = talkDepth;
      else if (purpose === 'monetise') {
        // Single regime field → both sexting money-makers are mutually
        // exclusive. Precedence: PPV > tips-in-sexting.
        if (monet.includes('ppv')) regime = 'talk_sext_and_sales_ladder';
        else if (monet.includes('tipsext')) regime = 'talk_sext_and_hot_tip';
      }
      if (regime) { payload.ai_base_regime = regime; payload.default_automation_regime = regime; }
      if (aggressionTouched) payload.ai_hot_transition_mode = HOT_MODE[aggression];
      await api.updateAccount(accountId, payload);

      if (monet.includes('tipchat')) {
        const cur = await api.getNormalTipPolicy(accountId);
        const body = { ...cur };
        delete body.exists;
        delete body.id;
        await api.putNormalTipPolicy(accountId, { ...body, is_active: true });
      }
      if (monet.includes('vip')) {
        const cur = await api.getVipOfferPolicy(accountId);
        const body = { ...cur };
        delete body.exists;
        delete body.id;
        await api.putVipOfferPolicy(accountId, { ...body, is_active: true, page_url: vipUrl.trim() || cur.page_url });
      }
    } catch (err) {
      console.error('AI setup apply failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const finishModelInfo = async () => { await applySettings(); go('ready'); };

  const allFolderOpts = folderOpts.length ? folderOpts : ['Notifications', 'Active spenders', 'VIP', 'Whales', 'New fans', 'Muted', 'Expired'];

  let content: React.ReactNode = null;

  if (step === 'purpose') content = (
    <Panel anim={animKey} width={620}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title k={t('aiSetup.kicker')} h={t('aiSetup.purpose.title')} sub={t('aiSetup.purpose.sub')} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Option layout="list" icon={<Icon name="chat" />} title={t('aiSetup.purpose.support.title')} desc={t('aiSetup.purpose.support.desc')} selected={purpose === 'support'} onClick={() => setPurpose('support')} />
        <Option layout="list" icon={<Icon name="ladder" />} title={t('aiSetup.purpose.monetise.title')} desc={t('aiSetup.purpose.monetise.desc')} selected={purpose === 'monetise'} onClick={() => setPurpose('monetise')} />
        <Option layout="list" icon={<Icon name="help" />} title={t('aiSetup.purpose.unsure.title')} desc={t('aiSetup.purpose.unsure.desc')} selected={purpose === 'unsure'} onClick={() => setPurpose('unsure')} />
      </div>
      <div style={{ marginTop: 24 }}>
        <Btn wide disabled={!purpose} style={{ opacity: purpose ? 1 : 0.5 }}
          onClick={() => purpose && (purpose === 'support' ? go('talkDepth') : purpose === 'monetise' ? go('monet') : go('aggression'))} icon="arrow">{t('aiSetup.continue')}</Btn>
      </div>
    </Panel>
  );
  else if (step === 'talkDepth') content = (
    <Panel anim={animKey} width={620}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title k={t('aiSetup.purpose.support.title')} h={t('aiSetup.talkDepth.title')} sub={t('aiSetup.talkDepth.sub')} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(['only_talk', 'talk_and_tease', 'talk_and_sext'] as TalkDepth[]).map((id) => (
          <Option key={id} layout="list" title={t(`aiSetup.talkDepth.${id}.title`)} desc={t(`aiSetup.talkDepth.${id}.desc`)} selected={talkDepth === id} onClick={() => setTalkDepth(id)} />
        ))}
      </div>
      <div style={{ marginTop: 24 }}>
        <Btn wide disabled={!talkDepth} style={{ opacity: talkDepth ? 1 : 0.5 }}
          onClick={() => talkDepth && (talkDepth === 'only_talk' ? go('folders') : go('aggression'))} icon="arrow">{t('aiSetup.continue')}</Btn>
      </div>
    </Panel>
  );
  else if (step === 'monet') content = (
    <Panel anim={animKey} width={620}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title k={t('aiSetup.purpose.monetise.title')} h={t('aiSetup.monet.title')} sub={t('aiSetup.monet.sub')} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {(['ppv', 'tipsext', 'tipchat', 'vip'] as Monet[]).map((id) => (
          <Option key={id} layout="list" title={t(`aiSetup.monet.${id}.title`)} desc={t(`aiSetup.monet.${id}.desc`)} selected={monet.includes(id)} onClick={() => toggleMonet(id)} />
        ))}
      </div>
      {monet.includes('ppv') && <p style={{ color: C.faint, fontSize: 12.5, marginTop: 12, lineHeight: 1.5 }}>{t('aiSetup.monet.ppvNote')}</p>}
      {monet.includes('vip') && (
        <div style={{ marginTop: 14 }}>
          <div style={{ color: C.faint, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{t('aiSetup.monet.vipUrlLabel')}</div>
          <input value={vipUrl} onChange={(e) => setVipUrl(e.target.value)} placeholder="https://onlyfans.com/your-vip"
            style={{ width: '100%', fontSize: 14.5, color: C.text, background: 'color-mix(in srgb, var(--om-ink) 4%, transparent)', border: `1px solid ${C.line2}`, borderRadius: 12, padding: '12px 14px', outline: 'none' }} />
        </div>
      )}
      <div style={{ marginTop: 22 }}>
        <Btn wide disabled={!monet.length} style={{ opacity: monet.length ? 1 : 0.5 }} onClick={() => monet.length && go('aggression')} icon="arrow">{t('aiSetup.continue')}</Btn>
      </div>
    </Panel>
  );
  else if (step === 'aggression') content = (
    <Panel anim={animKey} width={620}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title h={t('aiSetup.aggression.title')} sub={t('aiSetup.aggression.sub')} />
      <div className="mobile-stack-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        {(['shy', 'balanced', 'horny'] as Aggression[]).map((id) => (
          <Option key={id} title={t(`aiSetup.aggression.${id}.title`)} desc={t(`aiSetup.aggression.${id}.desc`)} selected={aggression === id} onClick={() => pickAggression(id)} />
        ))}
      </div>
      <div style={{ marginTop: 24 }}><Btn wide onClick={() => go('folders')} icon="arrow">{t('aiSetup.continue')}</Btn></div>
    </Panel>
  );
  else if (step === 'folders') content = (
    <Panel anim={animKey} width={560}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title h={t('aiSetup.folders.title')} sub={t('aiSetup.folders.sub')} />
      <div style={{ position: 'relative' }}>
        <div onClick={() => setFolderOpen((o) => !o)} className="om-opt" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '13px 16px', borderRadius: 12, cursor: 'pointer', background: 'color-mix(in srgb, var(--om-ink) 4%, transparent)', border: `1px solid ${C.line2}` }}>
          <span style={{ color: folders.length ? C.text : C.faint, fontSize: 14.5 }}>{folders.length ? t('aiSetup.folders.count', { count: folders.length }) : t('aiSetup.folders.placeholder')}</span>
          <Icon name="arrow" size={16} color={C.faint} />
        </div>
        {folderOpen && (
          <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 10, background: 'var(--om-card-2)', border: `1px solid ${C.line2}`, borderRadius: 12, padding: 6, maxHeight: 230, overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,.6)' }}>
            {foldersLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '9px 11px' }}>
                <Spinner size={16} label={t('aiSetup.folders.loading')} />
              </div>
            )}
            {allFolderOpts.map((f) => {
              const on = folders.includes(f);
              return (
                <div key={f} onClick={() => toggleFolder(f)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 9, cursor: 'pointer', background: on ? 'color-mix(in srgb, var(--om-accent) 14%, transparent)' : 'transparent' }}>
                  <div style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${on ? C.purple : C.line2}`, background: on ? C.purple : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{on && <Icon name="check" size={12} color="#fff" />}</div>
                  <span style={{ color: C.text, fontSize: 14 }}>{f}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {!!folders.length && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
          {folders.map((f) => <span key={f} onClick={() => toggleFolder(f)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 11px', borderRadius: 999, background: 'color-mix(in srgb, var(--om-accent) 14%, transparent)', border: '1px solid color-mix(in srgb, var(--om-accent) 32%, transparent)', color: C.purpleHi, fontSize: 13, cursor: 'pointer' }}>{f} ✕</span>)}
        </div>
      )}
      <p style={{ color: C.faint, fontSize: 12.5, marginTop: 14, lineHeight: 1.5 }}>{t('aiSetup.folders.note')}</p>
      <div style={{ marginTop: 22, display: 'flex', gap: 12 }}>
        <Btn onClick={() => go('telegram')} icon="arrow">{t('aiSetup.continue')}</Btn>
        <Btn kind="ghost" onClick={() => go('telegram')}>{t('aiSetup.folders.later')}</Btn>
      </div>
    </Panel>
  );
  else if (step === 'telegram') content = (
    <Panel anim={animKey} width={580}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title h={t('aiSetup.telegram.title')} sub={t('aiSetup.telegram.sub')} />
      <ol style={{ margin: '0 0 18px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <li key={i} style={{ display: 'flex', gap: 11, color: C.muted, fontSize: 14, lineHeight: 1.5 }}>
            <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'color-mix(in srgb, var(--om-accent) 18%, transparent)', color: C.purpleHi, fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
            <span>{t(`aiSetup.telegram.step${i + 1}`)}</span>
          </li>
        ))}
      </ol>
      <div style={{ color: C.faint, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{t('aiSetup.telegram.idLabel')}</div>
      <input value={tgId} onChange={(e) => { setTgId(e.target.value); setTgStatus('idle'); }} placeholder="-1001234567890"
        style={{ width: '100%', fontFamily: 'var(--font-mono)', fontSize: 15, color: C.text, background: 'color-mix(in srgb, var(--om-ink) 4%, transparent)', border: `1px solid ${tgStatus === 'ok' ? '#1f8a5b' : C.line2}`, borderRadius: 12, padding: '13px 15px', outline: 'none' }} />
      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        {tgStatus === 'ok'
          ? <Btn wide onClick={() => go('modelInfo')} icon="check">{t('aiSetup.telegram.allGood')}</Btn>
          : <Btn wide disabled={!tgId.trim() || tgStatus === 'sending'} className={tgStatus === 'sending' ? 'om-busy' : undefined} style={{ opacity: tgId.trim() ? 1 : 0.5 }} onClick={sendTelegramTest}>{t('aiSetup.telegram.sendTest')}</Btn>}
      </div>
      {tgStatus === 'sending' && <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, color: C.faint, fontSize: 13 }}><Spinner size={14} /> {t('aiSetup.telegram.sendingNote')}</div>}
      {tgStatus === 'ok' && <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 9, color: '#43c98a', fontSize: 13.5, fontWeight: 600 }}><Icon name="check" size={16} /> {t('aiSetup.telegram.sent')}</div>}
      {tgStatus === 'error' && <div style={{ marginTop: 14, color: '#ff6b6b', fontSize: 13 }}>{tgError}</div>}
      <div style={{ marginTop: 16 }}><a onClick={() => go('modelInfo')} style={{ color: C.faint, fontSize: 13, cursor: 'pointer' }}>{t('aiSetup.telegram.later')}</a></div>
    </Panel>
  );
  else if (step === 'modelInfo') content = (
    <Panel anim={animKey} width={560}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title h={t('aiSetup.modelInfo.title')} sub={t('aiSetup.modelInfo.sub')} />
      <div style={{ background: 'color-mix(in srgb, var(--om-ink) 4%, transparent)', border: `1px solid ${C.line}`, borderRadius: 14, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', color: C.muted, fontSize: 14.5 }}>
        <span style={{ color: C.text, fontWeight: 600 }}>{t('aiSetup.modelInfo.crumbAssistant')}</span>
        <Icon name="arrow" size={15} color={C.faint} />
        <span style={{ color: C.text, fontWeight: 600 }}>{t('aiSetup.modelInfo.crumbPersonality')}</span>
        <Icon name="arrow" size={15} color={C.faint} />
        <span style={{ color: C.text, fontWeight: 600 }}>{t('aiSetup.modelInfo.crumbInfo')}</span>
      </div>
      <p style={{ color: C.muted, fontSize: 14.5, lineHeight: 1.6, margin: '16px 0 0' }}>{t('aiSetup.modelInfo.body')}</p>
      <div style={{ marginTop: 24 }}>
        <Btn onClick={finishModelInfo} disabled={saving} icon={saving ? undefined : 'arrow'}><BusyLabel busy={saving} label={t('aiSetup.saving')}>{t('aiSetup.continue')}</BusyLabel></Btn>
      </div>
    </Panel>
  );
  else if (step === 'ready') content = (
    <Panel anim={animKey} width={540}>
      <div style={{ width: 54, height: 54, borderRadius: '50%', background: '#1f8a5b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(0,0,0,.5)', animation: 'om-pop .35s ease' }}><Icon name="check" size={28} /></div>
      <h1 style={{ color: C.text, fontSize: 27, fontWeight: 800, margin: '16px 0 0', letterSpacing: '-0.02em' }}>{t('aiSetup.ready.title')}</h1>
      <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.55, margin: '11px 0 0' }}>{t('aiSetup.ready.sub')}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 18 }}>
        {[1, 2].map((n) => (
          <div key={n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: 'color-mix(in srgb, var(--om-ink) 3.5%, transparent)', border: `1px solid ${C.line}`, borderRadius: 14, padding: '14px 16px' }}>
            <span style={{ flexShrink: 0, width: 24, height: 24, borderRadius: '50%', background: 'color-mix(in srgb, var(--om-accent) 18%, transparent)', color: C.purpleHi, fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
            <div>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 14.5 }}>{t(`aiSetup.ready.step${n}Title`)}</div>
              <div style={{ color: C.muted, fontSize: 13.5, marginTop: 3, lineHeight: 1.5 }}>{t(`aiSetup.ready.step${n}Desc`)}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <Btn onClick={() => navigate(`/account/${accountId}`, { replace: true })} icon="arrow">{t('aiSetup.ready.goToAccount')}</Btn>
        <Btn kind="ghost" onClick={toDashboard}>{t('aiSetup.ready.goToDashboard')}</Btn>
      </div>
    </Panel>
  );

  return (
    <OnboardingStage pose={POSE_FOR[step]} onClose={toDashboard}>
      {content}
    </OnboardingStage>
  );
}
